/**
 * Onboarding reference lookups — the `/v2/lookup/*` helpers that back the KYC/KYB form's
 * industry typeahead and address autocomplete. These are unauthenticated (called at signup
 * time before a partner key exists), so they go through {@link http}, not {@link request}.
 *
 * Source of truth: src/apps/external/src/v2/payouts/payouts.api.external.yaml (tag: Lookup)
 */
import { http, type RequestContext } from './client';

export type IndustrySuggestion = { naicsCode: string; title: string; subtitle?: string };

export type AddressParts = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

export type AddressSuggestion = { id: string; formattedText: string; addressParts?: AddressParts };

const qs = (params: Record<string, string | number | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') {
      search.set(k, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

/** GET /v2/lookup/industries — NAICS industry search for the industry typeahead. */
export async function lookupIndustries(
  env: RequestContext['env'],
  q: string,
  opts: { limit?: number; signal?: AbortSignal } = {}
): Promise<IndustrySuggestion[]> {
  const res = await http<{ data: IndustrySuggestion[] }>(env, `/lookup/industries${qs({ q, limit: opts.limit })}`, {
    signal: opts.signal,
  });
  return res?.data ?? [];
}

/** GET /v2/lookup/address/suggestions — address autocomplete. Domestic (US) suggestions carry `addressParts` inline. */
export async function lookupAddresses(
  env: RequestContext['env'],
  q: string,
  opts: { type?: 'domestic' | 'international'; country?: string; signal?: AbortSignal } = {}
): Promise<AddressSuggestion[]> {
  const res = await http<{ data: AddressSuggestion[] }>(
    env,
    `/lookup/address/suggestions${qs({ q, type: opts.type ?? 'domestic', country: opts.country })}`,
    { signal: opts.signal }
  );
  return res?.data ?? [];
}

/** GET /v2/lookup/address/places/{id} — resolve an international suggestion id to full address parts. */
export async function lookupAddressParts(
  env: RequestContext['env'],
  id: string,
  opts: { type?: 'domestic' | 'international'; signal?: AbortSignal } = {}
): Promise<{ id: string; addressParts: AddressParts }> {
  const res = await http<{ data: { id: string; addressParts: AddressParts } }>(
    env,
    `/lookup/address/places/${encodeURIComponent(id)}${qs({ type: opts.type ?? 'international' })}`,
    { signal: opts.signal }
  );
  return res.data;
}
